import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { useSubscriptionTier } from '@/hooks/useSubscriptionTier';
import { useScrollFab } from '@/hooks/useScrollFab';
import { useQuery, useQueryClient } from 'react-query';
import { useAppSelector } from '@/hooks/redux';
import { markAsRead } from '@/services/notificationService';
import { useNotifications } from '@/contexts/EventStreamContext';
import { clearPushNotifications } from '@/utils/clearPushNotifications';
import { toast } from 'react-toastify';
import {
  Typography,
  Box,
  Container,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Fab,
  Divider,
  Chip,
  Skeleton,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as WebsiteIcon,
  Person as PersonIcon,
  Sync as SyncIcon,
  SyncProblem as SyncProblemIcon,
  Error as ErrorIcon,
  Groups as GroupsIcon,
  EmojiEvents as LeaguesIcon,
  WorkspacePremium,
  Cancel as CancelIcon,
  FiberNew as NewIcon,
  Star as StarIcon,
  Visibility as MonitoringIcon,
  Delete as DeleteLeagueIcon,
} from '@mui/icons-material';
import { api } from '@/services/api';
import { leagueService, League as BaseLeague } from '@/services/leagueService';
import { OrganizationRole, UserRole } from '@/types/enums';
import PlayerAssignmentDialog from '@/components/PlayerAssignmentDialog';
import LeagueStandingsSection from '@/components/LeagueStandingsSection';
import { LeagueLogo } from '@/components/LeagueLogo';
import GotSportDiscoveryDialog from '@/components/GotSportDiscoveryDialog';
import SincSportsDiscoveryDialog from '@/components/SincSportsDiscoveryDialog';
import LeagueFormDialog, { LeagueFormData, DiscoveryPlatform } from '@/components/LeagueFormDialog';
import LeagueCatalogDialog, { CatalogDivisionSelection } from '@/components/LeagueCatalogDialog';
import { ScheduleGroup, SincSportsDivision } from '@/services/importService';
import { globalLeagueCatalogService } from '@/services/globalLeagueService';
import { getEffectiveTimezone } from '@/utils/timezoneUtils';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { syncSchedulerService } from '@/services/syncSchedulerService';
import { useSyncStream } from '@/contexts/EventStreamContext';
import { playerService } from '@/services/playerService';

interface SyncStats {
  totalMatches: number;
  newMatches: number;
  updatedMatches: number;
  uniqueTeams: number;
  dateRange?: {
    start: string;
    end: string;
  };
  syncDuration: number;
  lastSyncAt?: string;
  status: 'SUCCESS' | 'ERROR' | 'IN_PROGRESS' | 'NEVER_SYNCED';
  error?: string;
}

// Extend the base League interface with additional properties needed for this component
interface League extends BaseLeague {
  website?: string;
  logoSmallUrl?: string;
  logoMediumUrl?: string;
  logoPath?: string;
  seasonFormat?: string;
  playoffSpots?: number;
  importEnabled?: boolean;
  // Global league fields
  isGlobalLeague?: boolean;
  globalScheduleId?: string;
  globalSchedule?: {
    id: string;
    autoSyncEnabled: boolean;
    syncFrequency: string;
    syncTimeOfDay: string;
    syncPriority: number;
    defaultTimezone?: string;
    winPoints: number;
    drawPoints: number;
    lossPoints: number;
    nextSyncAt?: string;
    lastSyncAt?: string;
    lastSyncStatus?: string;
    lastSyncError?: string;
  };
  _count?: {
    matches: number;
    playerAssignments: number;
    teams: number;
  };
  syncStats?: SyncStats;
  // Sync configuration fields
  autoSyncEnabled?: boolean;
  syncTimeOfDay?: string;
  syncFrequency?: string;
  nextSyncAt?: string;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  lastSyncError?: string;
  syncPriority?: number;
  changeNotificationsEnabled?: boolean;
  // Season completion
  seasonCompleted?: boolean;
  seasonCompletedAt?: string;
  // Scoring configuration
  winPoints?: number;
  drawPoints?: number;
  lossPoints?: number;
  standingsSortBy?: string;
  tiebreakerRules?: string[];
  // Player assignments for standings
  playerAssignments?: Array<{
    player: { name: string };
    teamName: string;
  }>;
}

const LeagueManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppSelector((state) => state.auth);
  const { decrementUnreadCount } = useNotifications();
  const queryClient = useQueryClient();
  const fabRef = useScrollFab(100);
  
  // Redux selectors for timezone data
  const orgSettings = useSelector((state: RootState) => state.organization.settings);
  const { isAtLimit, hasFeature, limits } = useSubscriptionTier();
  
  // Fetch leagues with React Query - include org ID in key
  const { data: leaguesData = [], isLoading: loading } = useQuery(
    ['leagues', user?.currentOrganizationId],
    async () => {
      const response = await leagueService.getLeagues(1, 100);
      return (response.data || []) as League[];
    },
    {
      refetchInterval: 30 * 60 * 1000, // Refetch every 30 minutes (SSE handles real-time updates)
      enabled: !!user?.currentOrganizationId, // Only fetch if we have an org
      staleTime: 30 * 1000, // Consider data fresh for 30 seconds to prevent flicker on navigation
      refetchOnMount: true, // Refetch on mount only if stale (respects staleTime)
      keepPreviousData: true, // Keep previous data while loading new data
    }
  );

  const [_error, setError] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const actionParam = searchParams.get('action');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Handle URL params on mount
  useEffect(() => {
    if (actionParam === 'add') {
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
    const notificationId = searchParams.get('notificationId');
    if (notificationId) {
      clearPushNotifications();
      markAsRead([notificationId]).catch(() => {});
      decrementUnreadCount();
      searchParams.delete('notificationId');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editingLeague, setEditingLeague] = useState<League | null>(null);
  // Form data stored when discovery dialog is opened (for multi-league import)
  const [pendingFormData, setPendingFormData] = useState<LeagueFormData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedLeagueForAssignment, setSelectedLeagueForAssignment] = useState<League | null>(null);
  
  // Delete dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState<League | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  // Discovery dialog state
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);
  const [discoveryPlatform, setDiscoveryPlatform] = useState<DiscoveryPlatform>(null);

  // Catalog dialog state
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(actionParam === 'add');
  const [selectedCatalogDivision, setSelectedCatalogDivision] = useState<CatalogDivisionSelection | null>(null);

  // Players list for team picker mode in catalog dialog
  const { data: orgPlayers } = useQuery(
    ['players-for-picker', user?.currentOrganizationId],
    async () => {
      const players = await playerService.getPlayers();
      return players.map(p => ({ id: p.id, name: p.name }));
    },
    {
      enabled: !!user?.currentOrganizationId,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Track newly added league IDs (clears on page refresh since it's local state)
  const [recentlyAddedLeagueIds, setRecentlyAddedLeagueIds] = useState<Set<string>>(new Set());

  // Sync time preview state
  const [syncTimePreview, setSyncTimePreview] = useState<{
    nextSyncAt: string;
    displayTime: string;
    timezone: string;
  } | null>(null);




  // Memoize processed leagues grouped by category
  const groupedLeagues = useMemo(() => {
    if (!leaguesData) return { recentlyAdded: [], withPlayers: [], monitoring: [] };

    const processed = [...leaguesData].map((league: League) => ({
      ...league,
      syncStats: {
        totalMatches: league._count?.matches || 0,
        newMatches: 0,
        updatedMatches: 0,
        uniqueTeams: 0,
        syncDuration: 0,
        status: 'NEVER_SYNCED' as const
      },
    }));

    // Sort by league name, then by division
    const sortByNameThenDivision = (a: typeof processed[0], b: typeof processed[0]) => {
      const nameCompare = (a.name || '').localeCompare(b.name || '');
      if (nameCompare !== 0) return nameCompare;
      return (a.division || '').localeCompare(b.division || '');
    };

    const recentlyAdded: typeof processed = [];
    const withPlayers: typeof processed = [];
    const monitoring: typeof processed = [];

    for (const league of processed) {
      if (recentlyAddedLeagueIds.has(league.id)) {
        recentlyAdded.push(league);
      } else if ((league._count?.playerAssignments || 0) > 0) {
        withPlayers.push(league);
      } else {
        monitoring.push(league);
      }
    }

    // Sort each category
    recentlyAdded.sort(sortByNameThenDivision);
    withPlayers.sort(sortByNameThenDivision);
    monitoring.sort(sortByNameThenDivision);

    return { recentlyAdded, withPlayers, monitoring };
  }, [leaguesData, recentlyAddedLeagueIds]);

  // Standings refresh key for triggering re-renders
  const [standingsRefreshKey, setStandingsRefreshKey] = useState(0);

  // Track pending syncs for instant UI feedback (before SSE stream reports back)
  const [pendingSyncs, setPendingSyncs] = useState<Set<string>>(new Set());
  const [upgradeSnackbar, setUpgradeSnackbar] = useState(false);

  // Batch sync error toasts to avoid a flood when multiple leagues fail at once
  const syncBatchRef = useRef<{ errors: string[] }>({ errors: [] });
  const syncBatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushSyncBatch = useCallback(() => {
    const { errors } = syncBatchRef.current;
    syncBatchRef.current = { errors: [] };
    syncBatchTimerRef.current = null;

    if (errors.length === 1) {
      toast.error(errors[0]);
    } else if (errors.length > 1) {
      toast.error(`${errors.length} league updates failed.`);
    }
  }, []);

  // Sync callbacks for the unified event stream
  const syncCallbacks = useMemo(() => ({
    onSyncComplete: (leagueId: string, _result: any, event: any) => {
      // Clear pending state
      setPendingSyncs(prev => {
        const next = new Set(prev);
        next.delete(leagueId);
        return next;
      });
      // Suppress toasts for auto syncs — bell notification handles manual syncs
      if (event?.triggeredBy !== 'auto') {
        // No completion toast for manual syncs — the bell notification appears instead
      } else {
        // Auto syncs: completely silent
      }
      // Refresh standings
      setStandingsRefreshKey(prev => prev + 1);
    },
    onSyncError: (leagueId: string, error: string, event: any) => {
      // Clear pending state
      setPendingSyncs(prev => {
        const next = new Set(prev);
        next.delete(leagueId);
        return next;
      });
      // Only show error toasts for manual syncs
      if (event?.triggeredBy !== 'auto') {
        syncBatchRef.current.errors.push(error || 'Update failed');
        if (syncBatchTimerRef.current) clearTimeout(syncBatchTimerRef.current);
        syncBatchTimerRef.current = setTimeout(flushSyncBatch, 500);
      }
    }
  }), [flushSyncBatch]);

  // Organization-level sync stream via unified event stream
  const { syncingLeagues } = useSyncStream(syncCallbacks);

  // Refresh leagues when organization changes
  useOrganizationChange(() => {
    queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);
  });

  const handleSyncLeague = async (league: League) => {
    // Check if league has import configuration
    if (!league.website || !league.importEnabled) {
      toast.warning('League import is not enabled or website URL is not configured. Please edit the league to enable automatic importing.');
      return;
    }

    // Immediately show syncing state for instant UI feedback
    setPendingSyncs(prev => new Set(prev).add(league.id));

    try {
      // Trigger async sync - SSE stream will handle progress updates
      await syncSchedulerService.triggerManualSync(league.id);
      toast.info(`Update started for ${league.name}${league.division ? ' · ' + league.division : ''}`);
    } catch (err: any) {
      // Clear pending state on error
      setPendingSyncs(prev => {
        const next = new Set(prev);
        next.delete(league.id);
        return next;
      });
      console.error('Error starting sync:', err);
      // Handle 409 conflict (sync already in progress) gracefully
      if (err.response?.status === 409) {
        toast.info('An update is already in progress. Please wait for it to complete.');
      } else {
        toast.error(err.response?.data?.error?.message || 'Failed to start sync');
      }
    }
  };

  const getSyncStatusIcon = (league: League, syncStats?: any) => {
    // For catalog leagues, use GlobalSchedule sync status
    const status = league.globalSchedule?.lastSyncStatus || league.lastSyncStatus || syncStats?.status || 'NEVER_SYNCED';
    
    switch (status) {
      case 'success':
      case 'SUCCESS':
        return null; // No icon for success
      case 'error':
      case 'ERROR':
        return <ErrorIcon color="error" />;
      case 'in_progress':
      case 'IN_PROGRESS':
        return null; // No old spinner - we have new "Updating..." indicator
      case 'never':
      case 'NEVER_SYNCED':
        return <SyncProblemIcon color="disabled" />;
      default:
        return <SyncProblemIcon color="disabled" />;
    }
  };

  const getSyncStatusTooltip = (league: League, syncStats?: any) => {
    // For catalog leagues, use GlobalSchedule sync status
    const status = league.globalSchedule?.lastSyncStatus || league.lastSyncStatus || syncStats?.status || 'NEVER_SYNCED';
    const lastSyncAt = league.globalSchedule?.lastSyncAt || league.lastSyncAt || syncStats?.lastSyncAt;
    const error = league.globalSchedule?.lastSyncError || league.lastSyncError || syncStats?.error;

    const lastSyncText = lastSyncAt ? `Last update: ${formatLastSync(lastSyncAt)}` : 'Never imported';

    switch (status) {
      case 'success':
      case 'SUCCESS':
        return `✅ ${lastSyncText}`;
      case 'error':
      case 'ERROR':
        return `❌ Update failed\n${lastSyncText}${error ? `\nError: ${error}` : ''}`;
      case 'in_progress':
      case 'IN_PROGRESS':
        return '🔄 Update in progress...';
      case 'never':
      case 'NEVER_SYNCED':
        return '⚪ Never imported';
      default:
        return `⚠️ Unknown status\n${lastSyncText}`;
    }
  };

  const formatLastSync = (lastSyncAt?: string) => {
    if (!lastSyncAt) return 'Never';
    const date = new Date(lastSyncAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };


  const handleOpenDialog = (league?: League) => {
    setEditingLeague(league || null);
    if (!league) {
      // For new leagues, fetch sync time preview
      fetchSyncTimePreview();
    }
    setDialogOpen(true);
  };

  const handleCatalogImportSuccess = async (league: any, opts?: { teamName?: string; playerId?: string }) => {
    setCatalogDialogOpen(false);
    queryClient.invalidateQueries(['leagues']);
    setRecentlyAddedLeagueIds(prev => new Set([...prev, league.id]));

    // Auto-assign player to team if selected
    if (opts?.teamName && opts?.playerId) {
      try {
        await playerService.assignToLeague(opts.playerId, {
          leagueId: league.id,
          teamName: opts.teamName,
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && (key[0] === 'schedule' || key[0] === 'conflicts' || key[0] === 'players-for-picker');
          }
        });
        queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);
      } catch (assignErr) {
        console.error('Failed to auto-assign player:', assignErr);
        toast.warning('League created, but failed to assign player. You can assign manually.');
      }
    }

    // Trigger initial sync - SSE stream will track progress
    // Set pending state immediately for instant UI feedback
    setPendingSyncs(prev => new Set(prev).add(league.id));
    try {
      await syncSchedulerService.triggerManualSync(league.id);
      toast.success(
        `League "${league.name}" created! Schedule import started.`,
        { autoClose: 5000 }
      );
    } catch (syncErr) {
      console.error('Failed to start initial sync:', syncErr);
      // Clear pending state on error
      setPendingSyncs(prev => {
        const next = new Set(prev);
        next.delete(league.id);
        return next;
      });
      toast.success(`League "${league.name}" created! You can sync it manually.`);
    }
  };


  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLeague(null);
    setSyncTimePreview(null);
    setPendingFormData(null); // Clear pending data when closing the dialog
    setSelectedCatalogDivision(null); // Clear catalog selection
  };

  // Handle form submission from dialog
  const handleFormSubmit = async (formData: LeagueFormData) => {
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      if (formData.division) {
        formDataToSend.append('division', formData.division);
      }
      formDataToSend.append('description', formData.description);
      formDataToSend.append('website', formData.website);
      formDataToSend.append('logoUrl', formData.logo);
      formDataToSend.append('winPoints', formData.winPoints.toString());
      formDataToSend.append('drawPoints', formData.drawPoints.toString());
      formDataToSend.append('lossPoints', formData.lossPoints.toString());
      formDataToSend.append('playoffsEnabled', formData.playoffsEnabled.toString());
      if (formData.playoffsEnabled) {
        formDataToSend.append('playoffSpots', formData.playoffSpots.toString());
      }
      formDataToSend.append('standingsSource', formData.standingsSource);
      formDataToSend.append('importEnabled', 'true');
      formDataToSend.append('autoSyncEnabled', formData.autoSyncEnabled.toString());

      // Link to catalog division if coming from catalog
      if (selectedCatalogDivision) {
        formDataToSend.append('globalLeagueDivisionId', selectedCatalogDivision.division.id);
      }

      if (syncTimePreview && !editingLeague) {
        formDataToSend.append('nextSyncAt', syncTimePreview.nextSyncAt);
      }

      let leagueId: string;

      if (editingLeague) {
        await api.put(`/leagues/${editingLeague.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        leagueId = editingLeague.id;

        // Update sync configuration when editing
        if (formData.autoSyncEnabled) {
          try {
            await syncSchedulerService.updateLeagueSyncConfig(leagueId, {
              autoSyncEnabled: true,
              syncTimeOfDay: formData.syncTimeOfDay,
              syncFrequency: formData.syncFrequency as any,
              changeNotificationsEnabled: false,
              syncPriority: formData.syncPriority,
              timezone: getEffectiveTimezone(user, orgSettings)
            });
          } catch (syncError) {
            console.warn('Failed to update sync config:', syncError);
          }
        }

        queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);
      } else if (selectedCatalogDivision) {
        // Import from catalog using the catalog API
        const league = await globalLeagueCatalogService.importFromCatalog({
          divisionId: selectedCatalogDivision.division.id,
          name: formData.name, // User can customize the league name
          division: formData.division || undefined, // Division name
          logoUrl: formData.logo || undefined, // User can customize the logo
          playoffsEnabled: formData.playoffsEnabled,
          playoffSpots: formData.playoffsEnabled ? formData.playoffSpots : undefined,
          teams: selectedCatalogDivision.teams, // Pass pre-fetched teams for immediate availability
        });
        leagueId = league.id;
        queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);

        // Trigger initial sync - SSE stream will track progress
        // Set pending state immediately for instant UI feedback
        setPendingSyncs(prev => new Set(prev).add(leagueId));
        try {
          await syncSchedulerService.triggerManualSync(leagueId);
        } catch (syncErr) {
          console.error('Failed to start initial sync:', syncErr);
          // Clear pending state on error
          setPendingSyncs(prev => {
            const next = new Set(prev);
            next.delete(leagueId);
            return next;
          });
        }
      } else {
        // Custom league - use the regular leagues API
        const response = await api.post('/leagues', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        leagueId = response.data.id;
        queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);

        // Configure sync and trigger initial sync for new league
        // Note: Don't pass syncTimeOfDay - backend auto-assigns a randomized time during creation
        if (formData.autoSyncEnabled) {
          try {
            await syncSchedulerService.updateLeagueSyncConfig(leagueId, {
              autoSyncEnabled: true,
              syncFrequency: formData.syncFrequency as any,
              changeNotificationsEnabled: false,
              syncPriority: formData.syncPriority,
              timezone: getEffectiveTimezone(user, orgSettings)
            });
          } catch (syncError) {
            console.warn('Failed to configure sync:', syncError);
          }
        }

        // Trigger initial sync - SSE stream will track progress
        // Set pending state immediately for instant UI feedback
        setPendingSyncs(prev => new Set(prev).add(leagueId));
        try {
          await syncSchedulerService.triggerManualSync(leagueId);
        } catch (syncErr) {
          console.error('Failed to start initial sync:', syncErr);
          // Clear pending state on error
          setPendingSyncs(prev => {
            const next = new Set(prev);
            next.delete(leagueId);
            return next;
          });
        }
      }

      // Auto-assign player to team if selected from catalog
      if (!editingLeague && selectedCatalogDivision?.selectedTeamName && selectedCatalogDivision?.selectedPlayerId) {
        try {
          await playerService.assignToLeague(selectedCatalogDivision.selectedPlayerId, {
            leagueId,
            teamName: selectedCatalogDivision.selectedTeamName,
          });
          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && (key[0] === 'schedule' || key[0] === 'conflicts' || key[0] === 'players-for-picker');
            }
          });
          queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);
          toast.success(`${selectedCatalogDivision.selectedPlayerName || 'Player'} assigned to ${selectedCatalogDivision.selectedTeamName}`);
        } catch (assignErr) {
          console.error('Failed to auto-assign player:', assignErr);
          toast.warning('League created, but failed to assign player. You can assign manually.');
        }
      }

      // Track newly added leagues (not edits)
      if (!editingLeague) {
        setRecentlyAddedLeagueIds(prev => new Set([...prev, leagueId]));
      }

      toast.success(editingLeague ? 'League updated!' : 'League created!');
      handleCloseDialog();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to save league');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle discovery request from dialog (for GotSport/SincSports multi-league import)
  const handleDiscoveryRequest = (formData: LeagueFormData, platform: DiscoveryPlatform) => {
    setPendingFormData(formData);
    setDiscoveryPlatform(platform);
    setDialogOpen(false);
    setDiscoveryDialogOpen(true);
  };

  // Handle groups selected from GotSport discovery dialog
  const handleDiscoveryGroupsSelected = async (groups: ScheduleGroup[], eventName: string) => {
    if (groups.length === 0) return;
    if (!pendingFormData) {
      toast.error('Form data not available');
      return;
    }

    const formData = pendingFormData;
    setDiscoveryDialogOpen(false);
    setPendingFormData(null);
    let successCount = 0;
    let failCount = 0;
    const createdLeagueIds: string[] = [];

    toast.info(`Importing ${groups.length} league(s) from ${eventName}...`);

    // Helper to add random offset to sync time (spreads syncs over 60 minutes when importing multiple)
    const getRandomizedSyncTime = (baseTime: string, total: number): string => {
      if (total <= 1) return baseTime;
      // Add random offset between 0-59 minutes to spread out sync times
      const randomMinutes = Math.floor(Math.random() * 60);
      const [hours, minutes, seconds] = baseTime.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes + randomMinutes, seconds || 0);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${(seconds || 0).toString().padStart(2, '0')}`;
    };

    // Helper to add random offset to nextSyncAt ISO timestamp
    const getRandomizedNextSyncAt = (baseIsoTime: string, total: number): string => {
      if (total <= 1) return baseIsoTime;
      // Add random offset between 0-59 minutes
      const randomMinutes = Math.floor(Math.random() * 60);
      const date = new Date(baseIsoTime);
      date.setMinutes(date.getMinutes() + randomMinutes);
      return date.toISOString();
    };

    for (const group of groups) {
      try {
        // Create league name - use custom name if provided, otherwise use event + group name
        const leagueName = formData.name.trim()
          ? (groups.length > 1 ? `${formData.name} - ${group.name}` : formData.name)
          : `${eventName} - ${group.name}`;

        // Create the league with the schedule URL and form configurations
        const leagueFormData = new FormData();
        leagueFormData.append('name', leagueName);
        leagueFormData.append('description', formData.description || `Imported from GotSport: ${group.name}`);
        leagueFormData.append('website', group.scheduleUrl);
        leagueFormData.append('logoUrl', formData.logo);

        // Scoring configuration
        leagueFormData.append('winPoints', formData.winPoints.toString());
        leagueFormData.append('drawPoints', formData.drawPoints.toString());
        leagueFormData.append('lossPoints', formData.lossPoints.toString());

        // Playoff configuration
        leagueFormData.append('playoffsEnabled', formData.playoffsEnabled.toString());
        if (formData.playoffsEnabled) {
          leagueFormData.append('playoffSpots', formData.playoffSpots.toString());
        }

        leagueFormData.append('standingsSource', formData.standingsSource);
        leagueFormData.append('importEnabled', 'true');
        leagueFormData.append('autoSyncEnabled', formData.autoSyncEnabled.toString());

        // Include sync time preview if available (randomized for multi-import)
        if (syncTimePreview) {
          const nextSyncAt = getRandomizedNextSyncAt(syncTimePreview.nextSyncAt, groups.length);
          leagueFormData.append('nextSyncAt', nextSyncAt);
        }

        const response = await api.post('/leagues', leagueFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const leagueId = response.data.id;
        createdLeagueIds.push(leagueId);
        successCount++;

        // Configure sync settings if enabled (randomize sync time for multi-import)
        if (formData.autoSyncEnabled) {
          try {
            const syncTimeOfDay = getRandomizedSyncTime(formData.syncTimeOfDay, groups.length);
            await syncSchedulerService.updateLeagueSyncConfig(leagueId, {
              autoSyncEnabled: true,
              syncTimeOfDay,
              syncFrequency: formData.syncFrequency as any,
              changeNotificationsEnabled: false,
              syncPriority: formData.syncPriority,
              timezone: getEffectiveTimezone(user, orgSettings)
            });
          } catch (syncError) {
            console.warn(`Failed to configure sync for league ${leagueId}:`, syncError);
          }
        }

        // Trigger initial sync - SSE stream will track progress
        // Set pending state immediately for instant UI feedback
        setPendingSyncs(prev => new Set(prev).add(leagueId));
        try {
          await syncSchedulerService.triggerManualSync(leagueId);
        } catch (syncErr) {
          console.error('Failed to start initial sync:', syncErr);
          // Clear pending state on error
          setPendingSyncs(prev => {
            const next = new Set(prev);
            next.delete(leagueId);
            return next;
          });
        }
      } catch (err: any) {
        console.error(`Failed to import league for group ${group.name}:`, err);
        failCount++;
      }
    }

    // Track newly added leagues
    if (createdLeagueIds.length > 0) {
      setRecentlyAddedLeagueIds(prev => new Set([...prev, ...createdLeagueIds]));
    }

    // Refresh leagues list
    queryClient.invalidateQueries(['leagues']);

    if (failCount === 0) {
      toast.success(`Successfully imported ${successCount} league(s)!`);
    } else if (successCount > 0) {
      toast.warning(`Imported ${successCount} league(s), ${failCount} failed.`);
    } else {
      toast.error(`Failed to import leagues. Please try again.`);
    }
  };

  // Handle divisions selected from SincSports discovery dialog
  const handleSincSportsDivisionsSelected = async (divisions: SincSportsDivision[], leagueName: string) => {
    if (divisions.length === 0) return;
    if (!pendingFormData) {
      toast.error('Form data not available');
      return;
    }

    const formData = pendingFormData;
    setDiscoveryDialogOpen(false);
    setPendingFormData(null);
    setDiscoveryPlatform(null);
    let successCount = 0;
    let failCount = 0;
    const createdLeagueIds: string[] = [];

    toast.info(`Importing ${divisions.length} league(s) from ${leagueName}...`);

    // Helper to add random offset to sync time (spreads syncs over 60 minutes when importing multiple)
    const getRandomizedSyncTime = (baseTime: string, total: number): string => {
      if (total <= 1) return baseTime;
      // Add random offset between 0-59 minutes to spread out sync times
      const randomMinutes = Math.floor(Math.random() * 60);
      const [hours, minutes, seconds] = baseTime.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes + randomMinutes, seconds || 0);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${(seconds || 0).toString().padStart(2, '0')}`;
    };

    // Helper to add random offset to nextSyncAt ISO timestamp
    const getRandomizedNextSyncAt = (baseIsoTime: string, total: number): string => {
      if (total <= 1) return baseIsoTime;
      // Add random offset between 0-59 minutes
      const randomMinutes = Math.floor(Math.random() * 60);
      const date = new Date(baseIsoTime);
      date.setMinutes(date.getMinutes() + randomMinutes);
      return date.toISOString();
    };

    for (const division of divisions) {
      try {
        // Create league name - use custom name if provided, otherwise use league + division name
        const divisionLeagueName = formData.name.trim()
          ? (divisions.length > 1 ? `${formData.name} - ${division.name}` : formData.name)
          : `${leagueName} - ${division.name}`;

        // Create the league with the schedule URL and form configurations
        const leagueFormData = new FormData();
        leagueFormData.append('name', divisionLeagueName);
        leagueFormData.append('description', formData.description || `Imported from SincSports: ${division.name}`);
        leagueFormData.append('website', division.scheduleUrl);
        leagueFormData.append('logoUrl', formData.logo);

        // Scoring configuration
        leagueFormData.append('winPoints', formData.winPoints.toString());
        leagueFormData.append('drawPoints', formData.drawPoints.toString());
        leagueFormData.append('lossPoints', formData.lossPoints.toString());

        // Playoff configuration
        leagueFormData.append('playoffsEnabled', formData.playoffsEnabled.toString());
        if (formData.playoffsEnabled) {
          leagueFormData.append('playoffSpots', formData.playoffSpots.toString());
        }

        leagueFormData.append('standingsSource', formData.standingsSource);
        leagueFormData.append('importEnabled', 'true');
        leagueFormData.append('autoSyncEnabled', formData.autoSyncEnabled.toString());

        // Include sync time preview if available (randomized for multi-import)
        if (syncTimePreview) {
          const nextSyncAt = getRandomizedNextSyncAt(syncTimePreview.nextSyncAt, divisions.length);
          leagueFormData.append('nextSyncAt', nextSyncAt);
        }

        const response = await api.post('/leagues', leagueFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const leagueId = response.data.id;
        createdLeagueIds.push(leagueId);
        successCount++;

        // Configure sync settings if enabled (randomize sync time for multi-import)
        if (formData.autoSyncEnabled) {
          try {
            const syncTimeOfDay = getRandomizedSyncTime(formData.syncTimeOfDay, divisions.length);
            await syncSchedulerService.updateLeagueSyncConfig(leagueId, {
              autoSyncEnabled: true,
              syncTimeOfDay,
              syncFrequency: formData.syncFrequency as any,
              changeNotificationsEnabled: false,
              syncPriority: formData.syncPriority,
              timezone: getEffectiveTimezone(user, orgSettings)
            });
          } catch (syncError) {
            console.warn(`Failed to configure sync for league ${leagueId}:`, syncError);
          }
        }

        // Trigger initial sync - SSE stream will track progress
        // Set pending state immediately for instant UI feedback
        setPendingSyncs(prev => new Set(prev).add(leagueId));
        try {
          await syncSchedulerService.triggerManualSync(leagueId);
        } catch (syncErr) {
          console.error('Failed to start initial sync:', syncErr);
          // Clear pending state on error
          setPendingSyncs(prev => {
            const next = new Set(prev);
            next.delete(leagueId);
            return next;
          });
        }
      } catch (err: any) {
        console.error(`Failed to import league for division ${division.name}:`, err);
        failCount++;
      }
    }

    // Track newly added leagues
    if (createdLeagueIds.length > 0) {
      setRecentlyAddedLeagueIds(prev => new Set([...prev, ...createdLeagueIds]));
    }

    // Refresh leagues list
    queryClient.invalidateQueries(['leagues']);

    if (failCount === 0) {
      toast.success(`Successfully imported ${successCount} league(s)!`);
    } else if (successCount > 0) {
      toast.warning(`Imported ${successCount} league(s), ${failCount} failed.`);
    } else {
      toast.error(`Failed to import leagues. Please try again.`);
    }
  };

  const fetchSyncTimePreview = async () => {
    try {
      const userTimezone = getEffectiveTimezone(user, orgSettings);
      const preview = await syncSchedulerService.getSyncTimePreview(userTimezone);
      setSyncTimePreview(preview);
    } catch (error) {
      console.error('Failed to fetch sync time preview:', error);
      // Don't block league creation if preview fails
    }
  };

  const handleDeleteClick = (league: League) => {
    setLeagueToDelete(league);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leagueToDelete) return;

    try {
      setDeleteLoading(leagueToDelete.id);
      setDeleteConfirmOpen(false);
      
      await api.delete(`/leagues/${leagueToDelete.id}`);
      
      // Invalidate React Query cache to refresh the leagues list
      queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);
      
      toast.success(`League "${leagueToDelete.name}" deleted successfully`);
      
    } catch (err: any) {
      setError(err.response?.data?.error?.message || `Failed to delete ${leagueToDelete.name}`);
      console.error('Failed to delete league:', err);
    } finally {
      setDeleteLoading(null);
      setLeagueToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setLeagueToDelete(null);
  };

  const handleOpenAssignmentDialog = (league: League) => {
    setSelectedLeagueForAssignment(league);
    setAssignmentDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialogOpen(false);
    setSelectedLeagueForAssignment(null);
  };

  const handleAssignmentSuccess = () => {
    // Invalidate React Query cache to refresh the leagues list
    queryClient.invalidateQueries(['leagues', user?.currentOrganizationId]);
  };

  // Button style for action buttons
  const actionButtonSx = {
    fontSize: '11px',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 0,
    background: 'white',
    color: 'text.primary',
    borderColor: 'divider',
    '&:hover': { borderColor: 'divider', backgroundColor: 'white' },
    '&:disabled': {
      background: 'grey.100',
      color: 'text.disabled',
      borderColor: 'divider'
    },
    '&::before': {
      content: '""',
      background: 'linear-gradient(45deg, #ff0000, #ff7300, #fffb00, #48ff00, #00ffd5, #002bff, #7a00ff, #ff00c8, #ff0000)',
      position: 'absolute',
      top: '-1px',
      left: '-1px',
      backgroundSize: '400%',
      zIndex: -1,
      filter: 'blur(5px)',
      width: 'calc(100% + 2px)',
      height: 'calc(100% + 2px)',
      animation: 'glowing 20s linear infinite',
      opacity: 0,
      transition: 'opacity 0.3s ease-in-out',
      borderRadius: 1
    },
    '&:hover::before': { opacity: 1 },
    '&::after': {
      zIndex: -1,
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '100%',
      background: 'white',
      left: 0,
      top: 0,
      borderRadius: 1
    },
    '@keyframes glowing': {
      '0%': { backgroundPosition: '0 0' },
      '50%': { backgroundPosition: '400% 0' },
      '100%': { backgroundPosition: '0 0' }
    }
  };

  // Helper function to render league card action buttons and footer
  const renderLeagueCardActions = (league: League & { syncStats?: any }, isSyncing: boolean) => (
    <>
      <Box sx={{ px: 2, pb: 2, pt: 0, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, alignItems: 'start' }}>
        <Button
          size="small"
          startIcon={
            // Show eye icon for catalog leagues (read-only for all users), pencil otherwise
            league.isGlobalLeague
              ? <MonitoringIcon />
              : <EditIcon />
          }
          variant="outlined"
          onClick={() => handleOpenDialog(league)}
          disabled={!(
            !league.createdByParentId ||
            league.createdByParentId === user?.parentId ||
            user?.orgRole === OrganizationRole.OWNER ||
            user?.orgRole === OrganizationRole.ADMIN ||
            user?.globalRole === UserRole.SUPER_ADMIN ||
            league.isGlobalLeague // Allow viewing catalog leagues
          )}
          sx={actionButtonSx}
        >
          Settings
        </Button>
        <Tooltip
          title={
            (league._count?.teams || 0) === 0
              ? "Teams will be available after the first sync completes"
              : ""
          }
          arrow
        >
          <span style={{ display: 'contents' }}>
            <Button
              size="small"
              startIcon={<PersonIcon />}
              variant="outlined"
              onClick={() => handleOpenAssignmentDialog(league)}
              disabled={(league._count?.teams || 0) === 0}
              sx={actionButtonSx}
            >
              +Player
            </Button>
          </span>
        </Tooltip>
        <Button
          size="small"
          startIcon={<WebsiteIcon />}
          variant="outlined"
          component={league.website ? "a" : "button"}
          href={league.website || undefined}
          target={league.website ? "_blank" : undefined}
          rel={league.website ? "noopener noreferrer" : undefined}
          disabled={!league.website}
          sx={actionButtonSx}
        >
          Website
        </Button>
        <Button
          size="small"
          startIcon={hasFeature('position_analysis') ? <LeaguesIcon /> : <WorkspacePremium />}
          variant="outlined"
          onClick={() => hasFeature('position_analysis') ? navigate(`/leagues/${league.id}/analysis`) : setUpgradeSnackbar(true)}
          sx={{
            ...actionButtonSx,
            ...(!hasFeature('position_analysis') && {
              background: 'grey.100',
              color: 'text.disabled',
              borderColor: 'divider',
              '&:hover': { background: 'grey.100', borderColor: 'divider' },
            }),
          }}
        >
          Analysis
        </Button>
        <Button
          size="small"
          startIcon={
            <SyncIcon
              sx={{
                transform: 'scaleX(-1)',
                ...(isSyncing && {
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'scaleX(-1) rotate(0deg)' },
                    '100%': { transform: 'scaleX(-1) rotate(-360deg)' }
                  }
                })
              }}
            />
          }
          variant="outlined"
          onClick={() => handleSyncLeague(league)}
          disabled={isSyncing || !league.website}
          sx={{
            ...actionButtonSx,
            ...(isSyncing && {
              '& .MuiButton-startIcon': {
                color: 'primary.main'
              }
            })
          }}
        >
          <Box
            component="span"
            sx={isSyncing ? {
              display: 'inline-flex',
              transform: 'translateY(1px)',
              '& > span': {
                animation: 'wave 1.2s ease-in-out infinite',
                display: 'inline-block'
              },
              '& > span:nth-of-type(1)': { animationDelay: '0s' },
              '& > span:nth-of-type(2)': { animationDelay: '0.1s' },
              '& > span:nth-of-type(3)': { animationDelay: '0.2s' },
              '& > span:nth-of-type(4)': { animationDelay: '0.3s' },
              '& > span:nth-of-type(5)': { animationDelay: '0.4s' },
              '& > span:nth-of-type(6)': { animationDelay: '0.5s' },
              '@keyframes wave': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-2px)' }
              }
            } : {}}
          >
            {isSyncing ? (
              <>
                <span>U</span><span>p</span><span>d</span><span>a</span><span>t</span><span>e</span>
              </>
            ) : 'Update'}
          </Box>
        </Button>
        <Button
          size="small"
          startIcon={deleteLoading === league.id ? <CircularProgress size={16} /> : <DeleteIcon />}
          variant="outlined"
          onClick={() => handleDeleteClick(league)}
          disabled={deleteLoading === league.id || !(
            league.createdByParentId === user?.parentId ||
            user?.orgRole === OrganizationRole.OWNER ||
            user?.orgRole === OrganizationRole.ADMIN ||
            user?.globalRole === UserRole.SUPER_ADMIN
          )}
          sx={actionButtonSx}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ px: 2, pb: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {league.playoffsEnabled && league.playoffSpots && (
            <>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: '2px',
                  height: '16px',
                  backgroundColor: '#1976d2',
                  mr: 0.5,
                  borderRadius: '1px',
                  verticalAlign: '-3px'
                }}
              />
              = Playoffs
            </>
          )}
          {league.playoffsEnabled && league.playoffSpots && ' • '}Last update: {(() => {
            // For catalog leagues, use GlobalSchedule sync status
            const lastSync = league.globalSchedule?.lastSyncAt || league.lastSyncAt;
            if (!lastSync) return 'Never';
            const date = new Date(lastSync);
            const now = new Date();
            const diffMs = Math.abs(now.getTime() - date.getTime());
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);
            const diffDays = Math.floor(diffHours / 24);
            if (diffMins < 5) return 'Just now';
            if (diffMins < 60) return `${diffMins} min ago`;
            if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
            return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
          })()}
          {(() => {
            if (league.seasonCompleted) return ' • Season complete';
            // For catalog leagues, use GlobalSchedule for sync scheduling
            const autoSync = league.globalSchedule?.autoSyncEnabled ?? league.autoSyncEnabled;
            const nextSync = league.globalSchedule?.nextSyncAt || league.nextSyncAt;
            if (!autoSync || !nextSync) return null;
            const date = new Date(nextSync);
            const now = new Date();
            const diffMs = date.getTime() - now.getTime();
            if (diffMs <= 0) return ' • Next update in overdue';
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMins / 60);
            const diffDays = Math.floor(diffHours / 24);
            if (diffMins < 60) return ` • Next update in ${diffMins} min`;
            if (diffHours < 24) return ` • Next update in ${diffHours} hr${diffHours > 1 ? 's' : ''}`;
            return ` • Next update in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
          })()}
        </Typography>
      </Box>
    </>
  );

  return (
    <>
    <Container maxWidth={false} sx={{ py: 2, px: { xs: 1.5, sm: 3 }, pb: { xs: 10, sm: 4 }, position: 'relative' }}>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h4" component="h1">
            Leagues
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Manage your leagues and track team performance
        </Typography>

          {/* Add League Button */}
          {!loading && (leaguesData || []).length > 0 && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
              <Tooltip title={isAtLimit('leagues', (leaguesData || []).length) ? `Maximum of ${limits.maxLeagues} leagues reached` : ''}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCatalogDialogOpen(true)}
                    size="large"
                    disabled={isAtLimit('leagues', (leaguesData || []).length)}
                  >
                    Add League
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Box sx={{ minHeight: '400px' }}>
          {loading ? (
            /* Skeleton placeholders while loading */
            <Grid container spacing={3}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} lg={4} key={i}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Skeleton variant="rounded" width={48} height={48} sx={{ mr: 2, flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="70%" height={28} />
                          <Skeleton variant="text" width="40%" height={20} />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                        <Skeleton variant="rounded" width={70} height={24} />
                        <Skeleton variant="rounded" width={90} height={24} />
                      </Box>
                      <Skeleton variant="text" width="100%" />
                      <Skeleton variant="text" width="60%" />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (leaguesData || []).length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 4,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
            >
              <GroupsIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                No leagues created yet
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={4} maxWidth="sm" mx="auto">
                Add your first league to start tracking schedules and managing your players.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCatalogDialogOpen(true)}
                size="large"
              >
                Add Your First League
              </Button>
            </Box>
          ) : (
            <>
              {/* Recently Added Section */}
              {groupedLeagues.recentlyAdded.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <NewIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                      Recently Added
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({groupedLeagues.recentlyAdded.length})
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    {groupedLeagues.recentlyAdded.map((league) => {
                      const isSyncing = syncingLeagues.has(league.id) || pendingSyncs.has(league.id);
                      const syncStats = league.syncStats;
                      return (
                        <Grid item xs={12} sm={6} lg={4} key={league.id}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '2px solid', borderColor: 'success.light' }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box display="flex" alignItems="flex-start" mb={2}>
                                <Box sx={{ mr: 2 }}>
                                  <LeagueLogo
                                    leagueId={league.id}
                                    leagueName={league.name}
                                    size={60}
                                    logoPath={league.logoPath}
                                    logoUrl={league.logoUrl}
                                  />
                                </Box>
                                <Box flex={1} sx={{ minWidth: 0 }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography
                                      variant="h6"
                                      component="h2"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '260px',
                                        flex: 1,
                                        mr: 1
                                      }}
                                      title={league.name}
                                    >
                                      {league.name}
                                    </Typography>
                                    {(syncStats || league.lastSyncStatus) && !isSyncing && (
                                      <Tooltip title={getSyncStatusTooltip(league, syncStats)}>
                                        <Box display="flex" alignItems="center" ml={1}>
                                          {getSyncStatusIcon(league, syncStats)}
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '280px'
                                    }}
                                  >
                                    {league.division ? `${league.division} · ` : ''}{league._count?.matches || 0} matches
                                  </Typography>
                                  <Chip
                                    label={league.isGlobalLeague ? 'Catalog' : 'Custom'}
                                    size="small"
                                    color={league.isGlobalLeague ? 'primary' : 'default'}
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                  {league.seasonCompleted && (
                                    <Chip
                                      label="Season Complete"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <LeagueStandingsSection
                                leagueId={league.id}
                                playerAssignments={league.playerAssignments || []}
                                description={league.description}
                                leagueName={league.name}
                                refreshTrigger={standingsRefreshKey}
                                league={{
                                  playoffsEnabled: league.playoffsEnabled,
                                  playoffSpots: league.playoffSpots,
                                  standingsSortBy: league.standingsSortBy,
                                  standingsSource: league.standingsSource
                                }}
                              />
                            </CardContent>
                            {renderLeagueCardActions(league, isSyncing)}
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Active Leagues Section (with players assigned) */}
              {groupedLeagues.withPlayers.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  {(groupedLeagues.recentlyAdded.length > 0) && <Divider sx={{ mb: 3 }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                      Participating
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({groupedLeagues.withPlayers.length})
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    {groupedLeagues.withPlayers.map((league) => {
                      const isSyncing = syncingLeagues.has(league.id) || pendingSyncs.has(league.id);
                      const syncStats = league.syncStats;
                      return (
                        <Grid item xs={12} sm={6} lg={4} key={league.id}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box display="flex" alignItems="flex-start" mb={2}>
                                <Box sx={{ mr: 2 }}>
                                  <LeagueLogo
                                    leagueId={league.id}
                                    leagueName={league.name}
                                    size={60}
                                    logoPath={league.logoPath}
                                    logoUrl={league.logoUrl}
                                  />
                                </Box>
                                <Box flex={1} sx={{ minWidth: 0 }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography
                                      variant="h6"
                                      component="h2"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '260px',
                                        flex: 1,
                                        mr: 1
                                      }}
                                      title={league.name}
                                    >
                                      {league.name}
                                    </Typography>
                                    {(syncStats || league.lastSyncStatus) && !isSyncing && (
                                      <Tooltip title={getSyncStatusTooltip(league, syncStats)}>
                                        <Box display="flex" alignItems="center" ml={1}>
                                          {getSyncStatusIcon(league, syncStats)}
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '280px'
                                    }}
                                  >
                                    {league.division ? `${league.division} · ` : ''}{league._count?.matches || 0} matches
                                  </Typography>
                                  <Chip
                                    label={league.isGlobalLeague ? 'Catalog' : 'Custom'}
                                    size="small"
                                    color={league.isGlobalLeague ? 'primary' : 'default'}
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                  {league.seasonCompleted && (
                                    <Chip
                                      label="Season Complete"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <LeagueStandingsSection
                                leagueId={league.id}
                                playerAssignments={league.playerAssignments || []}
                                description={league.description}
                                leagueName={league.name}
                                refreshTrigger={standingsRefreshKey}
                                league={{
                                  playoffsEnabled: league.playoffsEnabled,
                                  playoffSpots: league.playoffSpots,
                                  standingsSortBy: league.standingsSortBy,
                                  standingsSource: league.standingsSource
                                }}
                              />
                            </CardContent>
                            {renderLeagueCardActions(league, isSyncing)}
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Monitoring Section (no players assigned) */}
              {groupedLeagues.monitoring.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  {(groupedLeagues.recentlyAdded.length > 0 || groupedLeagues.withPlayers.length > 0) && <Divider sx={{ mb: 3 }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MonitoringIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Watching
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({groupedLeagues.monitoring.length})
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    {groupedLeagues.monitoring.map((league) => {
                      const isSyncing = syncingLeagues.has(league.id) || pendingSyncs.has(league.id);
                      const syncStats = league.syncStats;
                      return (
                        <Grid item xs={12} sm={6} lg={4} key={league.id}>
                          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', opacity: 0.85 }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Box display="flex" alignItems="flex-start" mb={2}>
                                <Box sx={{ mr: 2 }}>
                                  <LeagueLogo
                                    leagueId={league.id}
                                    leagueName={league.name}
                                    size={60}
                                    logoPath={league.logoPath}
                                    logoUrl={league.logoUrl}
                                  />
                                </Box>
                                <Box flex={1} sx={{ minWidth: 0 }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography
                                      variant="h6"
                                      component="h2"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '260px',
                                        flex: 1,
                                        mr: 1
                                      }}
                                      title={league.name}
                                    >
                                      {league.name}
                                    </Typography>
                                    {(syncStats || league.lastSyncStatus) && !isSyncing && (
                                      <Tooltip title={getSyncStatusTooltip(league, syncStats)}>
                                        <Box display="flex" alignItems="center" ml={1}>
                                          {getSyncStatusIcon(league, syncStats)}
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '280px'
                                    }}
                                  >
                                    {league.division ? `${league.division} · ` : ''}{league._count?.matches || 0} matches
                                  </Typography>
                                  <Chip
                                    label={league.isGlobalLeague ? 'Catalog' : 'Custom'}
                                    size="small"
                                    color={league.isGlobalLeague ? 'primary' : 'default'}
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                  {league.seasonCompleted && (
                                    <Chip
                                      label="Season Complete"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              <LeagueStandingsSection
                                leagueId={league.id}
                                playerAssignments={league.playerAssignments || []}
                                description={league.description}
                                leagueName={league.name}
                                refreshTrigger={standingsRefreshKey}
                                league={{
                                  playoffsEnabled: league.playoffsEnabled,
                                  playoffSpots: league.playoffSpots,
                                  standingsSortBy: league.standingsSortBy,
                                  standingsSource: league.standingsSource
                                }}
                              />
                            </CardContent>
                            {renderLeagueCardActions(league, isSyncing)}
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
            </>
          )}
        </Box>

    </Container>

        {/* Add/Edit League Dialog - extracted to separate component for performance */}
        <LeagueFormDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleFormSubmit}
          onDiscoveryRequest={handleDiscoveryRequest}
          editingLeague={editingLeague}
          submitting={submitting}
          user={user}
          orgSettings={orgSettings}
          syncTimePreview={syncTimePreview}
          initialFormData={pendingFormData}
          fromCatalog={!!selectedCatalogDivision}
          readOnly={!!editingLeague?.isGlobalLeague}
          pendingAssignment={
            selectedCatalogDivision?.selectedTeamName && selectedCatalogDivision?.selectedPlayerName
              ? { playerName: selectedCatalogDivision.selectedPlayerName, teamName: selectedCatalogDivision.selectedTeamName }
              : undefined
          }
          onBack={() => {
            // Return to catalog dialog at step 2 (keep selected league, clear division)
            setPendingFormData(null);
            // Keep selectedCatalogDivision so we can pass globalLeague back to catalog
            setCatalogDialogOpen(true);
            setDialogOpen(false);
          }}
        />

        {/* Player Assignment Dialog */}
        {selectedLeagueForAssignment && (
          <PlayerAssignmentDialog
            open={assignmentDialogOpen}
            onClose={handleCloseAssignmentDialog}
            type="league"
            entityId={selectedLeagueForAssignment.id}
            entityName={selectedLeagueForAssignment.name}
            onSuccess={handleAssignmentSuccess}
          />
        )}



        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DeleteLeagueIcon />Delete League</Box></DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <strong>{leagueToDelete?.name}</strong>? 
              This will also delete all associated matches and team assignments. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleDeleteCancel} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleteLoading === leagueToDelete?.id}
              startIcon={deleteLoading === leagueToDelete?.id ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* GotSport Discovery Dialog */}
        <GotSportDiscoveryDialog
          open={discoveryDialogOpen && discoveryPlatform === 'gotsport'}
          onClose={() => {
            setDiscoveryDialogOpen(false);
            setPendingFormData(null);
            setDiscoveryPlatform(null);
          }}
          onBack={() => {
            // Return to Add League dialog
            setDiscoveryDialogOpen(false);
            setDiscoveryPlatform(null);
            setDialogOpen(true);
          }}
          initialUrl={pendingFormData?.website || ''}
          autoStart={true}
          onGroupsSelected={handleDiscoveryGroupsSelected}
        />

        {/* SincSports Discovery Dialog */}
        <SincSportsDiscoveryDialog
          open={discoveryDialogOpen && discoveryPlatform === 'sincsports'}
          onClose={() => {
            setDiscoveryDialogOpen(false);
            setPendingFormData(null);
            setDiscoveryPlatform(null);
          }}
          onBack={() => {
            // Return to Add League dialog
            setDiscoveryDialogOpen(false);
            setDiscoveryPlatform(null);
            setDialogOpen(true);
          }}
          initialUrl={pendingFormData?.website || ''}
          autoStart={true}
          onDivisionsSelected={handleSincSportsDivisionsSelected}
        />

        {/* League Catalog Dialog */}
        <LeagueCatalogDialog
          open={catalogDialogOpen}
          onClose={() => {
            setCatalogDialogOpen(false);
            setSelectedCatalogDivision(null); // Clear when fully closing
          }}
          onImport={handleCatalogImportSuccess}
          onCustomLeagueSubmit={(formData) => {
            // Handle custom league creation inline from the catalog dialog
            setCatalogDialogOpen(false);
            setSelectedCatalogDivision(null);
            handleFormSubmit(formData);
          }}
          onDiscoveryRequest={(formData, platform) => {
            // Handle discovery URL detection from inline custom form
            setPendingFormData(formData);
            setDiscoveryPlatform(platform);
            setCatalogDialogOpen(false);
            setDiscoveryDialogOpen(true);
          }}
          teamPickerMode
          players={orgPlayers}
          user={user}
          orgSettings={orgSettings}
        />

        {/* Floating Action Button */}
        <Fab
          ref={fabRef}
          color="primary"
          aria-label="add league"
          sx={{
            position: 'fixed',
            bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))', // 56px nav + 16px margin + safe area
            right: 16,
            display: { xs: 'flex', sm: 'none' },
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          onClick={() => setCatalogDialogOpen(true)}
        >
          <AddIcon />
        </Fab>

        <Snackbar
          open={upgradeSnackbar}
          autoHideDuration={3000}
          onClose={() => setUpgradeSnackbar(false)}
          message="Position analysis is available on the Family plan"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
    </>
  );
};

export default LeagueManagementPage;
